import React, { useCallback, useEffect, useRef, useState } from 'react';

// Custom confirm dialog, replacing window.confirm.
//
// The native dialog cannot be styled, shows the page origin, and looks like a browser warning
// rather than part of the product — not something to put in front of a user about to delete a
// track record. This keeps the same call shape so adopting it is a one-line change.
//
// Usage:
//   const [confirm, confirmDialog] = useConfirm();
//   if (await confirm({ title: 'Revoke this token?', ... })) { ... }
//   return (<>{confirmDialog}...</>);

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  onConfirm,
  onCancel,
}) {
  const confirmRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    // Focus the safe action, and let Escape dismiss — both behaviours people expect from the
    // native dialog and would otherwise lose.
    confirmRef.current?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape') onCancel?.();
      if (event.key === 'Enter') onConfirm?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel, onConfirm]);

  if (!open) return null;

  return (
    <div
      className="vix-confirm-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(event) => { if (event.target === event.currentTarget) onCancel?.(); }}
    >
      <div className={`vix-confirm vix-confirm-${tone}`}>
        <h4>{title}</h4>
        {body ? <p>{body}</p> : null}
        <div className="vix-confirm-actions">
          <button type="button" className="ghost-button compact-button" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={tone === 'danger' ? 'vix-confirm-danger' : 'primary-cta compact-button'}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Promise-based confirm, so call sites read like the native API they replace:
 *   if (!(await confirm({ title: '…' }))) return;
 */
export function useConfirm() {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((options) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setState(options || {});
  }), []);

  const settle = useCallback((result) => {
    setState(null);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(result);
  }, []);

  const dialog = (
    <ConfirmDialog
      open={!!state}
      title={state?.title || 'Are you sure?'}
      body={state?.body}
      confirmLabel={state?.confirmLabel}
      cancelLabel={state?.cancelLabel}
      tone={state?.tone}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );

  return [confirm, dialog];
}

export default ConfirmDialog;
